import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useNavigate } from "react-router";
import CustomButton from "./../Components/Utils/Custom_Btn";
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  CircularProgress,
  styled,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import deLocale from "@fullcalendar/core/locales/de";
import { fetchdateOfReservationAutocomplete } from "../Redux/Reservations_Slice";

const primaryColor = "#BE872A";
const secondaryColor = "#f5f5f5";

const StyleWrapper = styled(Box)(({ theme }) => ({
  "& .fc": {
    fontFamily: theme.typography.fontFamily,
    "& .fc-toolbar-title": {
      fontSize: "1.5rem",
      fontWeight: 600,
      color: primaryColor,
    },
    "& .fc-button": {
      backgroundColor: primaryColor,
      borderColor: primaryColor,
      color: "#fff",
      "&:hover": {
        backgroundColor: "#a57122",
        borderColor: "#a57122",
      },
    },
    "& .fc-daygrid-day": {
      transition: "all 0.3s",
      "&:hover": {
        backgroundColor: secondaryColor,
      },
    },
    "& .fc-daygrid-day.fc-day-today": {
      backgroundColor: `${primaryColor}20`,
    },
    "& .fc-event": {
      borderRadius: "8px",
      border: `1px solid ${primaryColor}99`,
      backgroundColor: primaryColor,
      color: "#fff",
      width: "100%",
      fontSize: "clamp(0.75rem, 1vw, 0.9rem)",
      padding: "4px 8px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "black",
        borderColor: "#1565c0",
        boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
        transform: "scale(1.02)",
      },
    },
    "&.fc-dayGridMonth-view .fc-col-header-cell": {
      display: "none",
    },
  },
}));

const KalenderReservierung = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = useSelector((state) => state.Reservations);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReservations, setSelectedReservations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allReservations, setAllReservations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dispatch(
          fetchdateOfReservationAutocomplete({ page: 1, limit: 1000 })
        ).unwrap();
        if (result.data) {
          setAllReservations(result.data.data || []);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungen:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  const transformReservationsToEvents = useCallback(() => {
    return allReservations.map((res) => {

      const startDate = new Date(res.dateOfReservation);
      const endDate = new Date(res.dateEndOfReservation);
  
      return {
        id: res._id,
        title: `Reservierung #${res.reservationNumber}`,
        start: startDate,
        end: endDate,
        extendedProps: { ...res },
        textColor: "#ffffff",
      };
    });
  }, [allReservations]);

  const handleEventClick = (info) => {
    const res = info.event.extendedProps;
    setSelectedDate(new Date(info.event.start));
    setSelectedReservations([res]);
    setDialogOpen(true);
    info.jsEvent.preventDefault();
  };

  const handleDateClick = (arg) => {
    const clicked = new Date(arg.dateStr);
    setSelectedDate(clicked);
    const reservationsForDate = allReservations.filter((r) => {
      const resDate = new Date(r.dateOfReservation);
      return (
        resDate.getFullYear() === clicked.getFullYear() &&
        resDate.getMonth() === clicked.getMonth() &&
        resDate.getDate() === clicked.getDate()
      );
    });
    setSelectedReservations(reservationsForDate);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  if (data.loading && !allReservations.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error && !allReservations.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Fehler beim Laden der Reservierungen: {data.error}
        </Typography>
      </Box>
    );
  }

  return (
    <StyleWrapper sx={{ height: "100%" }}>
      <CustomButton
        onNavigate={() => navigate("/AddBuchungen")}
        title="Neue Buchung"
      />
      <Paper elevation={3} sx={{ p: 2, height: "80vh", mt: 5 }}>
        <FullCalendar
          timeZone="UTC"
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          buttonText={{
            today: "Heute",
            month: "Monat",
            week: "Woche",
            day: "Tag",
            list: "Liste",
          }}
          locale={deLocale}
          events={transformReservationsToEvents()}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDisplay="block"
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          allDayText="gan. Tag"
          height="100%"
          nowIndicator
          selectable
          editable
          dayMaxEvents={1}
          eventContent={(info) => (
            <div>
              <b>{info.event.title}</b>
              <div>{info.event.extendedProps.userName}</div>
            </div>
          )}
          titleFormat={{ year: "numeric", month: "2-digit", day: "2-digit" }} 
        />
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reservierungsdetails für{" "}
          {selectedDate && format(selectedDate, "dd.MM.yyyy")}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReservations.length === 0 ? (
            <Typography>Keine Reservierungen gefunden</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {selectedReservations.map((res) => (
                <Paper key={res._id} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">
                      Reservierung #{res.reservationNumber}
                    </Typography>
                    <Chip
                      label={
                        res.status === "accepted"
                          ? "Bestätigt"
                          : res.status === "pending"
                          ? "Ausstehend"
                          : "Storniert"
                      }
                      color={
                        res.status === "accepted"
                          ? "success"
                          : res.status === "pending"
                          ? "warning"
                          : "error"
                      }
                    />
                  </Box>
                  <Typography>
                    <strong>Kunde:</strong> {res.userName}
                  </Typography>
                  <Typography>
                    <strong>Zeit:</strong>{" "}
                    {res.dateOfReservation.slice(11, 16)} -{" "}
                    {res.dateEndOfReservation.slice(11, 16)}
                  </Typography>
                  {res.packageId && (
                    <Typography>
                      <strong>Paket:</strong> {res.packageName}
                    </Typography>
                  )}
                  <Typography>
                    <strong>Personenanzahl:</strong> {res.numberOfPersons}
                  </Typography>
                  <Typography>

                  </Typography>
                  {res.extraObjectsList?.length > 0 && (
                    <Box sx={{display:"flex" }}>
                      <Typography variant="subtitle1">
                        <strong>Extras:</strong>
                      </Typography>
                      {res.extraObjectsList.map((item, index) => (
                        <Box key={index} sx={{ display: "flex", gap: 1 }}>
                          <Typography>
                            • {item.name} (Anzahl: {item.number}, Preis: {item.price}€)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                      
                  
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </StyleWrapper>
  );
};

export default KalenderReservierung;